import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * CurrencyContext — multi-currency display with persistent user preference.
 *
 * Backend prices are stored in USD (source of truth). Display currency is
 * a UI-only preference, persisted in localStorage. **Default is GBP** for
 * the current UK-focused launch; auto-detect from timezone is deliberately
 * disabled so foreign visitors don't accidentally create INR/AED bookings
 * (which would hide most wallets in Stripe Express Checkout).
 *
 * Customers can still switch via the header dropdown if they prefer to
 * browse in their home currency. The Pay button always converts back to
 * GBP server-side when the Stripe Checkout Session is created.
 */

const STORAGE_KEY = "companin_currency";
const AUTODETECT_KEY = "companin_currency_auto_detected_at";

// Conversion rates from USD (source-of-truth) to each display currency.
// Approximate; refresh quarterly. These are the same values used by the
// backend FX_RATES in server.py.
export const RATES = {
  GBP: { rate: 0.79,    symbol: "£", name: "British Pound", locale: "en-GB" },
  USD: { rate: 1.00,    symbol: "$", name: "US Dollar",     locale: "en-US" },
  EUR: { rate: 0.92,    symbol: "€", name: "Euro",          locale: "en-IE" },
  AUD: { rate: 1.52,    symbol: "A$", name: "Australian Dollar", locale: "en-AU" },
  CAD: { rate: 1.36,    symbol: "C$", name: "Canadian Dollar",   locale: "en-CA" },
  INR: { rate: 83.0,    symbol: "₹", name: "Indian Rupee",   locale: "en-IN" },
  AED: { rate: 3.67,    symbol: "د.إ", name: "UAE Dirham",   locale: "en-AE" },
  JPY: { rate: 156.0,   symbol: "¥", name: "Japanese Yen",   locale: "ja-JP" },
};

const DEFAULT_CURRENCY = "GBP";

// ISO country code → display currency. Used for first-visit auto-detect. Any
// country not listed falls back to GBP (the UK-launched platform default).
// Only includes the 8 currencies we already support — adding more would
// require new FX_RATES entries first.
const COUNTRY_TO_CURRENCY = {
  GB: "GBP", IE: "EUR",
  US: "USD", PR: "USD",
  DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", NL: "EUR", BE: "EUR",
  AT: "EUR", PT: "EUR", FI: "EUR", GR: "EUR", LU: "EUR",
  AU: "AUD", NZ: "AUD",
  CA: "CAD",
  IN: "INR",
  AE: "AED", SA: "AED", KW: "AED", QA: "AED",
  JP: "JPY",
};

// Fallback timezone heuristic when /api/geo/whoami fails. Maps a small set of
// well-known IANA zones to their dominant currency. Crude but free, and
// cached forever once we set it.
const TZ_TO_CURRENCY = {
  "Europe/London": "GBP", "Europe/Dublin": "EUR",
  "Europe/Paris": "EUR", "Europe/Berlin": "EUR", "Europe/Rome": "EUR",
  "Europe/Madrid": "EUR", "Europe/Amsterdam": "EUR", "Europe/Brussels": "EUR",
  "America/New_York": "USD", "America/Chicago": "USD", "America/Los_Angeles": "USD",
  "America/Denver": "USD", "America/Phoenix": "USD",
  "America/Toronto": "CAD", "America/Vancouver": "CAD",
  "Australia/Sydney": "AUD", "Australia/Melbourne": "AUD", "Pacific/Auckland": "AUD",
  "Asia/Kolkata": "INR", "Asia/Calcutta": "INR",
  "Asia/Dubai": "AED", "Asia/Riyadh": "AED", "Asia/Qatar": "AED",
  "Asia/Tokyo": "JPY",
};

// Iter45.10 — `navigator.language` cascade step. Maps BCP-47 language tags
// (e.g. `en-US`, `de-DE`, `en-IN`) to a display currency. This runs BETWEEN
// the server geo lookup and the timezone fallback, so a US traveller on a
// UK IP but with `en-US` locale still gets USD, and vice-versa.
const LANG_TO_CURRENCY = {
  "en-US": "USD", "es-US": "USD",
  "en-GB": "GBP", "cy-GB": "GBP",
  "en-IE": "EUR", "de-DE": "EUR", "fr-FR": "EUR", "it-IT": "EUR",
  "es-ES": "EUR", "nl-NL": "EUR", "pt-PT": "EUR", "el-GR": "EUR",
  "en-AU": "AUD", "en-NZ": "AUD",
  "en-CA": "CAD", "fr-CA": "CAD",
  "en-IN": "INR", "hi-IN": "INR", "ta-IN": "INR", "bn-IN": "INR",
  "ar-AE": "AED", "ar-SA": "AED", "ar-KW": "AED", "ar-QA": "AED",
  "ja-JP": "JPY", "ja": "JPY",
};

const CurrencyContext = createContext({
  currency: DEFAULT_CURRENCY,
  setCurrency: () => {},
  convert: (usd) => Number(usd) * RATES[DEFAULT_CURRENCY].rate,
  format: (usd) => `£${(Number(usd) * RATES[DEFAULT_CURRENCY].rate).toFixed(2)}`,
  symbol: RATES[DEFAULT_CURRENCY].symbol,
  rate: RATES[DEFAULT_CURRENCY].rate,
});

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_CURRENCY;
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && RATES[saved] ? saved : DEFAULT_CURRENCY;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, currency);
    } catch {}
  }, [currency]);

  // First-visit currency auto-detection.
  //
  // Strategy: when the user has NO saved preference, ask the backend's
  // /api/geo/whoami (which uses Cloudflare's CF-IPCountry header) for the
  // visitor's country. If that returns a country we recognise, snap the
  // currency to it. If the backend call fails (offline, blocked, etc.), fall
  // back to the visitor's IANA timezone.
  //
  // Runs once per browser. The `companin_currency_auto_detected_at`
  // timestamp blocks re-detection so the user's manual override (after the
  // first detection) is permanent.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const alreadySaved = localStorage.getItem(STORAGE_KEY);
    const alreadyDetected = localStorage.getItem(AUTODETECT_KEY);
    if (alreadySaved || alreadyDetected) return;

    let cancelled = false;
    const detect = async () => {
      let detected = null;
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const resp = await fetch(`${backendUrl}/api/geo/whoami`, {
          signal: AbortSignal.timeout?.(3000) || undefined,
        });
        if (resp.ok) {
          const j = await resp.json();
          const cc = (j?.country_code || "").toUpperCase();
          if (cc && COUNTRY_TO_CURRENCY[cc]) detected = COUNTRY_TO_CURRENCY[cc];
        }
      } catch { /* fall through to timezone heuristic */ }

      if (!detected) {
        // Iter45.10 — navigator.language cascade step.
        try {
          const lang = (navigator?.language || "").trim();
          if (lang && LANG_TO_CURRENCY[lang]) {
            detected = LANG_TO_CURRENCY[lang];
          } else {
            const base = lang.split("-")[0];
            if (base && LANG_TO_CURRENCY[base]) detected = LANG_TO_CURRENCY[base];
          }
        } catch (_langErr) {
          // navigator undefined (SSR) or exotic value — try next step
          void _langErr;
        }
      }

      if (!detected) {
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (tz && TZ_TO_CURRENCY[tz]) detected = TZ_TO_CURRENCY[tz];
        } catch { /* leave detected null */ }
      }

      if (cancelled) return;
      try {
        localStorage.setItem(AUTODETECT_KEY, new Date().toISOString());
      } catch {}
      if (detected && detected !== DEFAULT_CURRENCY && RATES[detected]) {
        setCurrencyState(detected);
      }
    };
    detect();
    return () => { cancelled = true; };
  }, []);

  const setCurrency = (next) => {
    if (RATES[next]) {
      setCurrencyState(next);
      // Mark as user-chosen so auto-detect never overwrites it later.
      try { localStorage.setItem(AUTODETECT_KEY, new Date().toISOString()); } catch {}
    }
  };

  const value = useMemo(() => {
    const info = RATES[currency] || RATES[DEFAULT_CURRENCY];
    const convert = (usd) => (Number(usd) || 0) * info.rate;
    const format = (usd, { compact = false } = {}) => {
      const amount = convert(usd);
      try {
        return new Intl.NumberFormat(info.locale, {
          style: "currency",
          currency,
          maximumFractionDigits: amount >= 100 ? 0 : 2,
          minimumFractionDigits: amount >= 100 || compact ? 0 : 2,
        }).format(amount);
      } catch {
        return `${info.symbol}${amount.toFixed(2)}`;
      }
    };
    // W6 — admin panel + ledger pages often carry per-row figures in their
    // original capture currency (e.g. a booking stored as ₹996 INR, the
    // Stripe Platform Balance in GBP, escrow tallies in USD). Funnel them
    // through this helper so the BIG figure renders in the admin's
    // selected display currency and the native amount is available as a
    // small hint underneath. `sourceCurrency` must be an ISO key present
    // in RATES; unknown source falls back to USD (the source-of-truth).
    const convertFrom = (amount, sourceCurrency = "USD") => {
      const src = RATES[String(sourceCurrency).toUpperCase()] || RATES.USD;
      // amount_in_USD = amount / src.rate. Then USD→display via convert().
      return ((Number(amount) || 0) / src.rate) * info.rate;
    };
    const formatFrom = (amount, sourceCurrency = "USD", { compact = false } = {}) => {
      const usd = (Number(amount) || 0) / ((RATES[String(sourceCurrency).toUpperCase()] || RATES.USD).rate);
      return format(usd, { compact });
    };
    const formatNative = (amount, sourceCurrency = "USD") => {
      const src = RATES[String(sourceCurrency).toUpperCase()] || RATES.USD;
      try {
        return new Intl.NumberFormat(src.locale, {
          style: "currency",
          currency: String(sourceCurrency).toUpperCase(),
          maximumFractionDigits: Number(amount) >= 100 ? 0 : 2,
        }).format(Number(amount) || 0);
      } catch {
        return `${src.symbol}${(Number(amount) || 0).toFixed(2)}`;
      }
    };
    return {
      currency,
      setCurrency,
      convert,
      convertFrom,
      format,
      formatFrom,
      formatNative,
      symbol: info.symbol,
      rate: info.rate,
    };
  }, [currency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => useContext(CurrencyContext);

/**
 * <Price> — display a monetary amount in the user's selected currency.
 *
 * Two props:
 *   usd      — amount in USD (the source-of-truth currency). Default mode.
 *   amount + sourceCurrency — amount in a non-USD source (e.g. a booking
 *                             stored in GBP, or Stripe balance in GBP).
 *                             The component converts to USD first, then
 *                             to the user's display currency.
 *   showSource (W6) — when true and sourceCurrency ≠ displayCurrency, render
 *                     a small grey "(₹996 source)" hint underneath. Used
 *                     in the admin panel so the forensic trail is visible
 *                     alongside the converted figure.
 */
export function Price({ usd, amount, sourceCurrency, compact = false, showSource = false, className = "" }) {
  const { format, formatFrom, formatNative, currency } = useCurrency();
  const displayValue = sourceCurrency != null
    ? formatFrom(amount, sourceCurrency, { compact })
    : format(usd, { compact });
  const showHint = showSource && sourceCurrency && String(sourceCurrency).toUpperCase() !== currency;
  if (!showHint) {
    return <span className={className} data-testid="price">{displayValue}</span>;
  }
  return (
    <span className={`inline-flex flex-col leading-tight ${className}`} data-testid="price">
      <span>{displayValue}</span>
      <span className="text-[10px] text-stone-400 font-normal">
        ({formatNative(amount, sourceCurrency)} source)
      </span>
    </span>
  );
}
